import { GoogleMap } from "@react-google-maps/api";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import BreadCrumbs from "../../../components/BreadCrumbs";
import ErrorMessageComponent from "../../../components/ErrorMessageComponent";
import LoadingMessage from "../../../components/LoadingMessage";
import {
  DeliveryZoneData,
  DeliveryZonePoint,
} from "../../../redux/deliverySlice";
import {
  useLazyGetRestaurantDetailsByIdQuery,
  useUpdateRestaurantHttpsMutation,
} from "../../../redux/services/restaurantServices";
import ControlPanel from "./ControlPanel";
import DrawingManager from "./Drawing";
import "./index.css";
import { useGoogleMaps } from "../../../hooks/useGoogleMaps";

// Default center coordinates (Colombo, Sri Lanka)
const DEFAULT_CENTER = { lat: 6.9271, lng: 79.8612 };

const MappingContainer: React.FC = () => {
  const [deliveryZone, setDeliveryZone] = useState<DeliveryZonePoint[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  // Set default center immediately
  const [initialCenter, setInitialCenter] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [originalDeliveryZone, setOriginalDeliveryZone] = useState<DeliveryZonePoint[]>([]);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const isSavedRef = useRef(isSaved);

  useEffect(() => {
    isSavedRef.current = isSaved;
  }, [isSaved]);

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Add validation for delivery zone coordinates
  const validateDeliveryZone = (zone: DeliveryZonePoint[]): boolean => {
    if (!zone || zone.length === 0) return true;
    
    return zone.every(point => {
      const lat = point.latitude;
      const lng = point.longitude;
      return (
        typeof lat === 'number' && 
        typeof lng === 'number' &&
        !isNaN(lat) && 
        !isNaN(lng) &&
        lat >= -90 && lat <= 90 &&
        lng >= -180 && lng <= 180
      );
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [updateRestaurantHttps, { isLoading, error }] =
    useUpdateRestaurantHttpsMutation();

  const [
    getRestaurantDetailsById,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { data: restaurantData, isLoading: isLoadingRestaurant },
  ] = useLazyGetRestaurantDetailsByIdQuery();

  const { isLoaded, loadError } = useGoogleMaps();

  // Toggle full screen mode
  const toggleFullScreen = () => {
    if (!mapContainerRef.current) return;

    if (!isFullScreen) {
      // Enter full screen
      if (mapContainerRef.current.requestFullscreen) {
        mapContainerRef.current.requestFullscreen();
      } else if ((mapContainerRef.current as any).webkitRequestFullscreen) {
        (mapContainerRef.current as any).webkitRequestFullscreen();
      } else if ((mapContainerRef.current as any).msRequestFullscreen) {
        (mapContainerRef.current as any).msRequestFullscreen();
      }
      setIsFullScreen(true);
    } else {
      // Exit full screen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
      setIsFullScreen(false);
    }
  };

  // Listen for full screen changes
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Fetch restaurant data when component mounts or restaurantId changes
  useEffect(() => {
    if (restaurantId) {
      getRestaurantDetailsById(restaurantId);
    }
  }, [restaurantId, getRestaurantDetailsById]);

  // Extract delivery zone from restaurant data with validation
  useEffect(() => {
    if (restaurantData && restaurantData.restaurant) {
      if (
        restaurantData.restaurant.deliveryZone &&
        restaurantData.restaurant.deliveryZone.length > 0
      ) {
        const zone = restaurantData.restaurant.deliveryZone;
        
        if (validateDeliveryZone(zone)) {
          setDeliveryZone(zone);
          setOriginalDeliveryZone(zone);
          setIsSaved(true);
          
          // Calculate center from zone points
          const avgLat = zone.reduce((sum: number, point: any) => sum + point.latitude, 0) / zone.length;
          const avgLng = zone.reduce((sum: number, point: any) => sum + point.longitude, 0) / zone.length;
          setInitialCenter({ lat: avgLat, lng: avgLng });
        } else {
          toast.error("Invalid delivery zone data found. Please redraw the zone.");
          setDeliveryZone([]);
          setOriginalDeliveryZone([]);
          setIsSaved(false);
        }
      } else {
        setDeliveryZone([]);
        setOriginalDeliveryZone([]);
        setIsSaved(false);
        setInitialCenter(DEFAULT_CENTER);
      }
    }
  }, [restaurantData]);

  const handlePolygonComplete = useCallback(
    (polygonData: DeliveryZonePoint[]) => {
      if (!validateDeliveryZone(polygonData)) {
        toast.error("Invalid polygon coordinates. Please try again.");
        return;
      }
      
      setDeliveryZone(polygonData);
      setIsSaved(false);
    },
    []
  );

  const handlePolygonEdit = useCallback((polygonData: DeliveryZonePoint[]) => {
    if (!validateDeliveryZone(polygonData)) {
      toast.error("Invalid polygon coordinates after edit.");
      return;
    }
    
    setDeliveryZone(polygonData);
    setIsSaved(false);
  }, []);

  const handleDrawingModeChange = useCallback((drawing: boolean) => {
    setIsDrawing(drawing);
  }, []);

  const toggleDrawing = () => {
    // Disable drawing if there are existing points
    if (deliveryZone.length > 0 && !isDrawing) {
      toast.info("Please clear the current delivery zone first to draw a new one.");
      return;
    }
    setIsDrawing(!isDrawing);
  };

  const clearPolygons = () => {
    if (deliveryZone.length === 0) {
      toast.info("No delivery zone to clear!");
      return;
    }

    // Exit full screen before showing modal
    if (isFullScreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
      setIsFullScreen(false);
      
      // Small delay to ensure full screen has exited
      setTimeout(() => {
        showClearConfirmation();
      }, 300);
    } else {
      showClearConfirmation();
    }
  };

  const showClearConfirmation = () => {
    Swal.fire({
      title: "Clear Delivery Zone?",
      text: "Are you sure you want to clear the current delivery zone?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, clear it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setDeliveryZone([]);
        setIsSaved(false);
        toast.success("Delivery zone cleared successfully!");
      }
    });
  };

  const showSaveConfirmation = () => {
    // Check if there's an existing zone that will be replaced
    const hasExistingZone = originalDeliveryZone.length > 0;

    Swal.fire({
      title: "Save Delivery Zone?",
      text: hasExistingZone 
        ? "This will replace your existing delivery zone. Do you want to continue?"
        : "Do you want to save this delivery zone?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, save it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const deliveryZoneData: DeliveryZoneData = {
            deliveryZone,
          };

          await updateRestaurantHttps({
            restaurantId,
            data: deliveryZoneData,
          }).unwrap();

          toast.success("Delivery zone saved successfully!");
          setIsSaved(true);
          setOriginalDeliveryZone(deliveryZone); 

          if (restaurantId) {
            getRestaurantDetailsById(restaurantId);
          }
        } catch (error) {
          toast.error("Failed to save delivery zone");
          setIsSaved(false);
        }
      } else {
        // User cancelled - revert to original zone
        cancelNewDrawing();
      }
    });
  };

  // Function to cancel new drawing and revert to original zone
  const cancelNewDrawing = () => {
    setDeliveryZone(originalDeliveryZone);
    setIsSaved(true);
    toast.info("New delivery zone discarded. Original zone restored.");
  };

  const savePolygons = async () => {
    if (deliveryZone.length === 0) {
      toast.error("No delivery zone to save!");
      return;
    }

    if (!validateDeliveryZone(deliveryZone)) {
      toast.error("Invalid delivery zone coordinates. Cannot save.");
      return;
    }

    if (isSavedRef.current) {
      toast.info("Delivery zone already saved!");
      return;
    }

    // Exit full screen before showing modal
    if (isFullScreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
      setIsFullScreen(false);
      
      // Small delay to ensure full screen has exited
      setTimeout(() => {
        showSaveConfirmation();
      }, 300);
    } else {
      showSaveConfirmation();
    }
  };

  // Add effect to detect changes to delivery zone and reset saved state
  useEffect(() => {
    if (isSaved && deliveryZone.length > 0) {
      const originalZone = restaurantData?.restaurant?.deliveryZone || [];
      if (JSON.stringify(deliveryZone) !== JSON.stringify(originalZone)) {
        setIsSaved(false);
      }
    }
  }, [deliveryZone, isSaved, restaurantData]);

  if (loadError) {
    return (
      <div>
        <ErrorMessageComponent />
        <p>Error loading Google Maps: {loadError.message}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div>
        <LoadingMessage />
      </div>
    );
  }

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div 
        className={`map-container-deliv-zone ${isFullScreen ? 'full-screen' : ''}`}
        ref={mapContainerRef}
      >
        <div className="status-bar">
          <div className="status-content">
            {isDrawing && (
              <span className="drawing-status">
                Click on the map to start drawing a delivery zone. Click the first point again to complete.
              </span>
            )}
            {!isDrawing && deliveryZone.length === 0 && (
              <span className="idle-status">
                Click "Draw Delivery Zone" to start drawing.
              </span>
            )}
            {!isDrawing && deliveryZone.length > 0 && (
              <span className="polygons-status">
                {isSaved
                  ? `Existing delivery zone loaded with ${deliveryZone.length} points. If you want to create a new one, first clear the current zone.`
                  : `New delivery zone created with ${deliveryZone.length} points. Click "Save Delivery Zone" to save.`}
              </span>
            )}
          </div>
          
          <button 
            onClick={toggleFullScreen}
            className="full-screen-toggle"
            title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
          >
            {isFullScreen ? "❌" : "⛶"}
          </button>
        </div>

        <div className="map-wrapper">
          <GoogleMap
            center={initialCenter}
            zoom={13}
            mapContainerStyle={{ 
              width: "100%", 
              height: isFullScreen ? "calc(100vh - 60px)" : "70vh" 
            }}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
            onLoad={(map) => {
              setMap(map);
            }}
            onUnmount={() => {
              setMap(null);
            }}
          >
            {map && (
              <DrawingManager
                map={map}
                isDrawing={isDrawing}
                deliveryZone={deliveryZone}
                onPolygonComplete={handlePolygonComplete}
                onDrawingModeChange={handleDrawingModeChange}
                onPolygonEdit={handlePolygonEdit}
              />
            )}
          </GoogleMap>

          <ControlPanel
            isDrawing={isDrawing}
            polygonsCount={deliveryZone.length}
            onToggleDrawing={toggleDrawing}
            onClearPolygons={clearPolygons}
            onSavePolygons={savePolygons}
            isLoading={isLoading}
            isSaved={isSaved}
            isFullScreen={isFullScreen}
            onToggleFullScreen={toggleFullScreen}
          />
        </div>
      </div>
    </>
  );
};

export default MappingContainer;